import { useState } from "react";
import client, { imgUrl } from "@/lib/glossyApi";
import { toast } from "sonner";
import { RefreshCw, Upload, X, ScanFace, Sparkles, Send, Trash2, RotateCw } from "lucide-react";

const TARGETS = [
  { value: "directory_image", label: "Cover Member (masthead)" },
  { value: "cover_image", label: "Editorial cover" },
  { value: "advert_image", label: "Advert image" },
  { value: "insta_tile_image", label: "Instagram tile" },
];

const Step = ({ n, title, note, children }) => (
  <div className="border border-ink/15 bg-white p-5" data-testid={`photoflow-step-${n}`}>
    <p className="overline text-gloss-gold">Step {n}</p>
    <p className="font-serif text-xl mt-0.5">{title}</p>
    {note && <p className="font-sans text-xs text-ink/55 mt-1 mb-4">{note}</p>}
    {!note && <div className="mb-4" />}
    {children}
  </div>
);

// The Gloss — their own casual photo in, studio-finished version out. Keep or try again; nothing saved until Keep.
const GlossPanel = ({ m }) => {
  const [job, setJob] = useState(null);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const pick = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = async () => {
        const MAX = 1800;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        const b64 = c.toDataURL("image/jpeg", 0.92);
        setState("working"); setError("");
        try {
          const r = await client.post(`/admin/members/${m.id}/gloss-photo`, { image_b64: b64 });
          poll(r.data.job_id);
        } catch (e) { setState("idle"); toast.error(e?.response?.data?.detail || "Couldn't start."); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const poll = async (jobId) => {
    try {
      const r = await client.get(`/admin/members/${m.id}/gloss-photo/${jobId}`);
      if (r.data.status === "ready") { setJob({ ...r.data, id: jobId }); setState("ready"); }
      else if (r.data.status === "failed") { setState("idle"); setError(r.data.error || "The studio couldn't finish this one — try again."); }
      else setTimeout(() => poll(jobId), 4000);
    } catch { setTimeout(() => poll(jobId), 6000); }
  };

  const keep = async () => {
    try {
      await client.post(`/admin/members/${m.id}/gloss-photo/${job.id}/keep`);
      toast.success("Kept — it's in their Shot Gallery (Step 3), ready to use anywhere.");
      setJob(null); setState("idle");
    } catch { toast.error("Couldn't save."); }
  };

  return (
    <div className="border border-gloss-gold/50 bg-white p-5" data-testid="gloss-panel">
      <p className="overline text-gloss-gold">The Gloss</p>
      <p className="font-serif text-xl mt-0.5">Their photo, studio finish</p>
      <p className="font-sans text-xs text-ink/55 mt-1 mb-4">Upload any of their normal photos — same person, same clothes, same setting — and the studio re-lights, re-grades and retouches it to magazine standard. Nothing is saved until you press Keep.</p>
      {state === "idle" && (
        <>
          <label className="inline-flex items-center gap-2 border border-ink px-5 py-3 overline cursor-pointer hover:bg-ink hover:text-cream transition-colors" data-testid="gloss-upload-btn">
            <Upload size={14} /> Upload their photo to gloss
            <input type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="gloss-file-input" />
          </label>
          {error && <p className="font-sans text-xs text-red-600 mt-3" data-testid="gloss-error">{error}</p>}
        </>
      )}
      {state === "working" && (
        <p className="font-sans text-sm text-ink flex items-center gap-2" data-testid="gloss-working"><RefreshCw size={14} className="animate-spin text-gloss-gold" /> In the studio — usually under a minute…</p>
      )}
      {state === "ready" && job && (
        <div data-testid="gloss-result">
          <div className="grid grid-cols-2 gap-4 max-w-xl">
            <div>
              <p className="overline text-[10px] text-ink/50 mb-1.5">Before</p>
              <img src={imgUrl({ cover_image: job.original })} alt="Before" className="w-full aspect-[3/4] object-cover border border-ink/15" data-testid="gloss-before" />
            </div>
            <div>
              <p className="overline text-[10px] text-gloss-gold mb-1.5">After — the gloss</p>
              <img src={imgUrl({ cover_image: job.image })} alt="After" className="w-full aspect-[3/4] object-cover border-2 border-gloss-gold" data-testid="gloss-after" />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={keep} className="bg-ink text-cream px-6 py-3 overline hover:bg-gloss-gold hover:text-ink transition-colors" data-testid="gloss-keep-btn">Keep it</button>
            <label className="border border-ink/40 px-6 py-3 overline cursor-pointer hover:border-ink transition-colors" data-testid="gloss-retry-btn">
              Try again with another photo
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { setJob(null); pick(e.target.files?.[0]); }} />
            </label>
            <button onClick={() => { setJob(null); setState("idle"); }} className="overline text-ink/50 hover:text-ink transition-colors" data-testid="gloss-discard-btn">Discard</button>
          </div>
        </div>
      )}
    </div>
  );
};

// The simple path for a total re-edit: photos in → fresh re-shoot → pick keepers → use everywhere.
export default function PhotoFlow({ member: m, setMember, onChange }) {
  const [busy, setBusy] = useState("");
  const [cmPrompt, setCmPrompt] = useState("Fresh, edgy cover-star portrait — bold colour backdrop, modern lighting with attitude, relaxed confident pose, full of character. Cool British indie-magazine energy, never corporate, never stiff");
  const [edPrompt, setEdPrompt] = useState("Vogue/GQ editorial fashion cover — clean elegant studio lighting, rich natural colour, high-end fashion styling, confident cover-star presence. Real fashion photography: NO smoke, NO haze, NO fog, NO coloured gel lighting, NO theatrical effects.");
  const photos = m.photos || [];
  const workPhotos = m.work_photos || [];
  const keepers = [...(m.shot_gallery || [])].reverse().slice(0, 8);
  const rs = m.reshoot_all || {};
  const running = rs.status === "running";

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy("upload");
    const fd = new FormData();
    Array.from(files).slice(0, 5).forEach((f) => fd.append("files", f));
    try {
      const r = await client.post(`/admin/members/${m.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMember({ ...m, photos: r.data.photos });
      toast.success("Photos in — remove any old ones so the studio only works from the new set.");
      onChange();
    } catch { toast.error("Upload failed."); }
    setBusy("");
  };

  const removePhoto = async (path) => {
    try {
      const r = await client.post(`/admin/members/${m.id}/photos/remove`, { path, field: "photos" });
      setMember({ ...m, photos: r.data.photos });
    } catch { toast.error("Couldn't remove that photo."); }
  };

  const rotatePhoto = async (path, field) => {
    try {
      const r = await client.post(`/admin/members/${m.id}/rotate-photo`, { path, field });
      const upd = { [field]: r.data[field] };
      if (m.likeness_ref === path) upd.likeness_ref = r.data.path;
      setMember({ ...m, ...upd });
    } catch { toast.error("Couldn't rotate that photo."); }
  };

  const uploadWork = async (files) => {
    if (!files?.length) return;
    setBusy("workupload");
    const fd = new FormData();
    Array.from(files).slice(0, 10).forEach((f) => fd.append("files", f));
    try {
      const r = await client.post(`/admin/members/${m.id}/work-photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMember({ ...m, work_photos: r.data.work_photos });
      toast.success("Work photos in — use them in articles, adverts and covers.");
      onChange();
    } catch { toast.error("Upload failed."); }
    setBusy("");
  };

  const removeWork = async (path) => {
    try {
      const r = await client.post(`/admin/members/${m.id}/photos/remove`, { path, field: "work_photos" });
      setMember({ ...m, work_photos: r.data.work_photos });
    } catch { toast.error("Couldn't remove that photo."); }
  };

  const faceLock = async (path) => {
    try {
      const r = await client.post(`/admin/members/${m.id}/likeness`, { path });
      setMember({ ...m, likeness_ref: r.data.likeness_ref });
      toast.success("Face locked — every shoot anchors on this exact photo.");
    } catch { toast.error("Couldn't set the face lock."); }
  };

  const startOver = async () => {
    if (!window.confirm(`Start ${m.business_name} completely over? This wipes ALL their photos, the face lock, the pinned Look, both covers and the studio gallery — a totally clean slate for new uploads.`)) return;
    setBusy("startover");
    try {
      const r = await client.post(`/admin/members/${m.id}/fresh-reshoot`, { clear_photos: true });
      setMember({ ...m, ...r.data.member });
      toast.success("Clean slate — upload their new photos, then press Fresh re-shoot.");
      onChange();
    } catch { toast.error("Couldn't wipe — try again."); }
    setBusy("");
  };

  const freshReshoot = async () => {
    if (photos.length < 3) { toast.error("Upload at least 3 fresh photos first (Step 1)."); return; }
    if (!window.confirm("Fresh re-shoot: wipes the old face lock, pinned Look, old covers and studio gallery, then re-shoots their ENTIRE imagery from ONLY the photos in Step 1. Several generations from your budget. Go?")) return;
    setBusy("reshoot");
    try {
      const r = await client.post(`/admin/members/${m.id}/fresh-reshoot`, { clear_photos: false });
      setMember({ ...m, ...r.data.member });
      await client.post(`/admin/members/${m.id}/reshoot-all`);
      setMember((p) => ({ ...p, reshoot_all: { status: "running", done: 0, total: 0 } }));
      toast.success("Old anchors wiped — the studio is re-shooting everything from the NEW photos only. Watch the progress below.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start the re-shoot."); }
    setBusy("");
  };

  const uploadCover = async (preset, file) => {
    if (!file) return;
    const target = preset === "directory" ? "directory_image" : "cover_image";
    setBusy(`${preset}-upload`);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await client.post(`/admin/members/${m.id}/cover-upload?target=${target}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMember((p) => ({ ...p, [target]: r.data[target] }));
      toast.success(preset === "directory"
        ? "Their real photo is now the Cover Member — framed and stamped with the S/ME masthead."
        : "Their real photo is now the Editorial cover.");
      onChange();
    } catch { toast.error("Upload failed — try another image."); }
    setBusy("");
  };

  const shootCover = async (preset) => {
    if (photos.length < 3) { toast.error("Upload at least 3 photos first (Step 1)."); return; }
    const label = preset === "directory" ? "Cover Member portrait" : "Editorial cover";
    if (!window.confirm(`Shoot a fresh ${label} of THEM from the Step 1 photos? Spends AI credits.`)) return;
    setBusy(preset);
    try {
      const r = await client.post(`/admin/members/${m.id}/generate-cover`, { preset, style: preset === "directory" ? cmPrompt : edPrompt });
      const target = preset === "directory" ? "directory_image" : "cover_image";
      setMember((p) => ({ ...p, [target]: r.data.image, shot_gallery: r.data.shot ? [...(p.shot_gallery || []), r.data.shot] : p.shot_gallery }));
      toast.success(`${label} shot — it's live on their profile and saved in the keepers below.`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Shoot failed — try again."); }
    setBusy("");
  };

  const binKeeper = async (id) => {
    if (!window.confirm("Bin this shot from the studio gallery?")) return;
    try {
      const r = await client.post(`/admin/members/${m.id}/shot-gallery/remove`, { id, everywhere: false });
      setMember((p) => ({ ...p, shot_gallery: r.data.shot_gallery }));
    } catch { toast.error("Couldn't bin that shot."); }
  };

  const sendTo = async (image, target) => {
    setBusy("assign");
    try {
      const r = await client.post(`/admin/members/${m.id}/shot-gallery/assign`, { image, target });
      setMember({ ...m, ...(r.data.member || {}) });
      toast.success(`Set as the ${TARGETS.find((t) => t.value === target)?.label}.`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't assign."); }
    setBusy("");
  };

  const sendEverywhere = async (image) => {
    if (!window.confirm("Use this shot EVERYWHERE — Cover Member, Editorial cover, Advert and Instagram tile?")) return;
    setBusy("everywhere");
    let latest = m;
    for (const t of TARGETS) {
      try {
        const r = await client.post(`/admin/members/${m.id}/shot-gallery/assign`, { image, target: t.value });
        latest = { ...latest, ...(r.data.member || {}) };
      } catch { /* best effort per slot */ }
    }
    setMember(latest);
    toast.success("Done — this shot is now their face everywhere.");
    onChange();
    setBusy("");
  };

  return (
    <div className="space-y-4" data-testid="photo-flow">
      <Step n={1} title="Photos in" note="Their newest real photos ONLY — remove anything old so the studio never looks at it. Tap the face icon to lock the definitive face photo.">
        <div className="flex flex-wrap gap-3">
          {photos.map((p, i) => (
            <div key={p} className="w-24">
              <div className={`relative w-24 h-28 border-2 ${m.likeness_ref === p ? "border-gloss-gold" : "border-ink/15"}`} data-testid={`photoflow-photo-${i}`}>
                <img src={imgUrl({ cover_image: p })} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removePhoto(p)} className="absolute -top-2 -right-2 w-5 h-5 bg-ink text-cream flex items-center justify-center hover:bg-red-600 transition-colors" title="Remove" data-testid={`photoflow-remove-${i}`}>
                  <X size={11} />
                </button>
                <button onClick={() => faceLock(p)} className={`absolute bottom-1 left-1 w-6 h-6 flex items-center justify-center ${m.likeness_ref === p ? "bg-gloss-gold text-ink" : "bg-ink/70 text-cream hover:bg-ink"}`} title={m.likeness_ref === p ? "This is the locked face" : "Lock the face on this photo"} data-testid={`photoflow-facelock-${i}`}>
                  <ScanFace size={13} />
                </button>
                <button onClick={() => rotatePhoto(p, "photos")} className="absolute bottom-1 right-1 w-6 h-6 flex items-center justify-center bg-ink/70 text-cream hover:bg-gloss-gold hover:text-ink transition-colors" title="Rotate 90° clockwise" data-testid={`photoflow-rotate-${i}`}>
                  <RotateCw size={12} />
                </button>
              </div>
              <select onChange={(e) => { if (e.target.value) { sendTo(p, e.target.value); e.target.value = ""; } }} defaultValue="" disabled={!!busy}
                className="mt-1 w-full overline text-[8px] border border-ink/20 py-1 px-0.5 bg-white cursor-pointer hover:border-ink transition-colors disabled:opacity-50" data-testid={`photoflow-photo-usein-${i}`}>
                <option value="">Use this…</option>
                {TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          ))}
          <label className="w-24 h-28 border-2 border-dashed border-ink/25 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-ink transition-colors" data-testid="photoflow-upload">
            {busy === "upload" ? <RefreshCw size={16} className="animate-spin text-ink/50" /> : <Upload size={16} className="text-ink/50" />}
            <span className="font-mono text-[9px] uppercase tracking-wider text-ink/50">Add photos</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} disabled={!!busy} />
          </label>
        </div>
        <button onClick={startOver} disabled={!!busy || running} className="mt-4 overline text-[10px] border border-ink/25 text-ink/55 px-3 py-2 flex items-center gap-1.5 hover:border-red-600 hover:text-red-600 transition-colors disabled:opacity-50" data-testid="photoflow-start-over">
          <Trash2 size={12} /> Start completely over (wipe all photos + old studio anchors)
        </button>
        <div className="mt-5 border-t border-ink/10 pt-4" data-testid="photoflow-work-photos">
          <p className="overline text-[10px] text-ink/60 mb-2">Their work & product photos (up to 20 — separate from the 5 portraits; use anywhere)</p>
          <div className="flex flex-wrap gap-3">
            {workPhotos.map((p, i) => (
              <div key={p} className="w-20">
                <div className="relative w-20 h-20 border border-ink/15" data-testid={`photoflow-work-${i}`}>
                  <img src={imgUrl({ cover_image: p })} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeWork(p)} className="absolute -top-2 -right-2 w-5 h-5 bg-ink text-cream flex items-center justify-center hover:bg-red-600 transition-colors" title="Remove" data-testid={`photoflow-work-remove-${i}`}>
                    <X size={11} />
                  </button>
                  <button onClick={() => rotatePhoto(p, "work_photos")} className="absolute bottom-1 right-1 w-5 h-5 flex items-center justify-center bg-ink/70 text-cream hover:bg-gloss-gold hover:text-ink transition-colors" title="Rotate 90° clockwise" data-testid={`photoflow-work-rotate-${i}`}>
                    <RotateCw size={11} />
                  </button>
                </div>
                <select onChange={(e) => { if (e.target.value) { sendTo(p, e.target.value); e.target.value = ""; } }} defaultValue="" disabled={!!busy}
                  className="mt-1 w-full overline text-[8px] border border-ink/20 py-1 px-0.5 bg-white cursor-pointer hover:border-ink transition-colors disabled:opacity-50" data-testid={`photoflow-work-usein-${i}`}>
                  <option value="">Use this…</option>
                  {TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            ))}
            <label className="w-20 h-20 border-2 border-dashed border-ink/25 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-ink transition-colors" data-testid="photoflow-work-upload">
              {busy === "workupload" ? <RefreshCw size={14} className="animate-spin text-ink/50" /> : <Upload size={14} className="text-ink/50" />}
              <span className="font-mono text-[8px] uppercase tracking-wider text-ink/50">Add</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadWork(e.target.files)} disabled={!!busy} />
            </label>
          </div>
        </div>
      </Step>

      <GlossPanel m={m} />

      <Step n={2} title="Fresh re-shoot" note="One press: forgets everything old (face lock, pinned Look, old covers, gallery) and re-shoots their Cover Member, Editorial cover, every article image and the advert from ONLY the photos above.">
        {running ? (
          <div data-testid="photoflow-progress">
            <div className="flex items-center gap-3">
              <RefreshCw size={15} className="animate-spin text-gloss-gold" />
              <p className="font-sans text-sm text-ink">Re-shooting now — {rs.done || 0} of {rs.total || "…"} pieces done. New shots land in Step 3 as they finish.</p>
            </div>
            <div className="h-2 bg-ink/10 mt-3 relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-gloss-gold transition-all" style={{ width: `${rs.total ? Math.round(((rs.done || 0) / rs.total) * 100) : 5}%` }} />
            </div>
          </div>
        ) : (
          <button onClick={freshReshoot} disabled={!!busy} className="w-full bg-ink text-cream py-4 overline flex items-center justify-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="photoflow-fresh-reshoot">
            {busy === "reshoot" ? <RefreshCw size={15} className="animate-spin" /> : <Sparkles size={15} />}
            {busy === "reshoot" ? "Starting…" : "Fresh re-shoot — everything, from the new photos only"}
          </button>
        )}
        {rs.status === "done" && <p className="font-sans text-xs text-green-700 mt-2" data-testid="photoflow-done-note">Last re-shoot finished — pick the keepers below.</p>}
        {rs.status === "failed" && <p className="font-sans text-xs text-red-600 mt-2">The last re-shoot hit a problem — press it again.</p>}
      </Step>

      <Step n={3} title="The two covers — always of THEM" note="Two clean shots of the person, each with its own direction. Cover Member = the masthead portrait on the Directory. Editorial = the hero photo inside their feature.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { preset: "directory", label: "Cover Member", img: m.directory_image, prompt: cmPrompt, setPrompt: setCmPrompt },
            { preset: "editorial", label: "Editorial cover", img: m.cover_image, prompt: edPrompt, setPrompt: setEdPrompt },
          ].map((c) => (
            <div key={c.preset} className="border border-ink/15 p-3" data-testid={`photoflow-cover-${c.preset}`}>
              <p className="overline text-[10px] text-ink mb-2">{c.label}</p>
              <div className="aspect-[2/3] max-w-[180px] bg-secondary overflow-hidden mb-2">
                {c.img ? <img src={imgUrl({ cover_image: c.img })} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center font-mono text-[9px] uppercase tracking-wider text-ink/35">not shot yet</div>}
              </div>
              <textarea value={c.prompt} onChange={(e) => c.setPrompt(e.target.value)} rows={3}
                className="w-full border border-ink/25 px-3 py-2 font-sans text-xs focus:outline-none focus:border-ink"
                data-testid={`photoflow-prompt-${c.preset}`} />
              <button onClick={() => shootCover(c.preset)} disabled={!!busy || running}
                className="mt-2 w-full bg-ink text-cream py-2.5 overline text-[10px] flex items-center justify-center gap-1.5 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50"
                data-testid={`photoflow-shoot-${c.preset}`}>
                {busy === c.preset ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {busy === c.preset ? "Shooting…" : `Shoot ${c.label}`}
              </button>
              <label className="mt-1.5 w-full border border-ink/25 py-2.5 overline text-[10px] flex items-center justify-center gap-1.5 cursor-pointer hover:border-ink transition-colors"
                data-testid={`photoflow-upload-${c.preset}`}>
                {busy === `${c.preset}-upload` ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
                {busy === `${c.preset}-upload` ? "Uploading…" : `Upload their real photo${c.preset === "directory" ? " — S/ME masthead added" : ""}`}
                <input type="file" accept="image/*" className="hidden" disabled={!!busy}
                  onChange={(e) => { uploadCover(c.preset, e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
          ))}
        </div>
      </Step>

      <Step n={4} title="Pick the keepers" note="The latest studio shots, newest first. Bin what you don't like.">
        {keepers.length === 0 ? (
          <p className="font-sans text-sm text-ink/45">Nothing shot yet — run the Fresh re-shoot above.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="photoflow-keepers">
            {keepers.map((g, i) => (
              <div key={g.id || g.image} className="border border-ink/15 relative" data-testid={`photoflow-keeper-${i}`}>
                <button onClick={() => binKeeper(g.id)} className="absolute -top-2 -right-2 z-10 w-5 h-5 bg-ink text-cream flex items-center justify-center hover:bg-red-600 transition-colors" title="Bin this shot" data-testid={`photoflow-keeper-bin-${i}`}>
                  <X size={11} />
                </button>
                <div className="aspect-[3/4] bg-secondary overflow-hidden">
                  <img src={imgUrl({ cover_image: g.image })} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-2 space-y-1.5">
                  <button onClick={() => sendEverywhere(g.image)} disabled={!!busy} className="w-full bg-ink text-cream overline text-[9px] py-2 flex items-center justify-center gap-1.5 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50" data-testid={`photoflow-everywhere-${i}`}>
                    <Send size={11} /> Use everywhere
                  </button>
                  <select onChange={(e) => { if (e.target.value) { sendTo(g.image, e.target.value); e.target.value = ""; } }} defaultValue="" disabled={!!busy}
                    className="w-full overline text-[9px] border border-ink/25 py-1.5 px-1 bg-white cursor-pointer hover:border-ink transition-colors disabled:opacity-50" data-testid={`photoflow-usein-${i}`}>
                    <option value="">Use in one place…</option>
                    {TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
      </Step>
    </div>
  );
}
